/**
 * WavesAutotune - a composite key + scale selector for a Waves autotune
 * plugin. Two horizontal key rows plus a five-cell scale selector
 * (CHR / MAJ / MIN / HARM / DOR).
 *
 * The two key rows are ergonomic alternate layouts of ONE key selection:
 * row 1 anchored on `row1Home` (default A → "Am"), row 2 on `row2Home`
 * (default E → "Em"), so both preferred roots sit at the home column.
 * Each row is a chromatic ramp `home ± offset` for offsets `start..end`.
 * Tapping any cell sends the SAME session key (relights it wherever it
 * appears in both rows); the scale selector is an independent radio.
 *
 * The scale selector's placement is configurable (`scalePlacement`):
 * bottom/top render it as a full-width row (3 rows tall); left/right render
 * it as a compact vertical column beside the key rows (2 rows tall, the
 * buttons sharing that height with `flex: 1` each).
 *
 * ⚠️ SIDE PLACEMENT IS THE CRAMPED ONE AND IT GOT TIGHTER WITH DORIAN. The
 * column's height is fixed at two key-row bands by `wavesAutotuneDims`, so
 * five buttons now split what three split when it was designed - roughly 20px
 * a button at M size, down from 26 at four. Bottom/top placement is unaffected
 * (a wider row, not a shorter one). Not redesigned here because changing the
 * side height would reflow every already-authored layout using it; flagged so
 * the next person does not discover it on stage.
 *
 * Selection is session-wide (`autotune/session.ts`), NOT per-item - so
 * multiple WavesAutotune items stay in lock-step and neither key nor scale
 * survives a per-item remount. Selecting dispatches MIDI CC on the primary
 * bridge (key = CC 55, scale = CC 56) on the item's own `channel`.
 *
 * Independent of Transpose/Capo by design. Edit mode yields to the wrapper
 * (select/drag); test + performance modes fire.
 */

import { For, Show, type Accessor } from 'solid-js';
import { editOwnsGesture, isEditMode, type WavesAutotuneItem, type LayoutColor } from '../layout/store.ts';
import {
  setAutotuneKey,
  setAutotuneScale,
  autotuneStrength, setAutotuneStrength,
  noteName, type AutotuneScale,
} from '../autotune/session.ts';
import { createDiscreteGuard } from '../performance/guard.ts';
import { autotuneKeyShown, autotuneScaleShown } from '../autotune/readback.ts';
import { songAutotuneStatus, songAutotuneWhy } from '../setlist/state.ts';
import { TrayTitle, trayAttrs } from './TrayTitle.tsx';

interface WavesAutotuneProps {
  item: WavesAutotuneItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

/** Five modes as of 2026-09-11 (DORIAN joined the four from 2026-09-01,
 *  operator ask: "2 minors, harmonic and natural").
 *
 *  ORDERED TO MATCH WAVES TUNE'S OWN SCALE LIST so the row reads the way the
 *  plugin does - Chromatic, Major, Natural Minor, Harmonic Minor and Dorian
 *  are list entries 2, 3, 4, 5 and 10. DORIAN therefore lands LAST, not
 *  beside the minors it is modally closest to. That is deliberate: the row's
 *  one ordering rule is the plugin's list, and a row sorted by music theory
 *  instead would disagree with the dropdown the operator reads when something
 *  looks wrong.
 *
 *  Labels are the operator's language, not the plugin's: MIN / HARM / DOR
 *  rather than "Natural Minor" / "Harmonic Minor" / "Dorian", because the row
 *  is now FIVE cells wide on an iPad and a gigging musician reads the short
 *  form faster. Going to five made the short form more load-bearing, not
 *  less - each cell is a fifth of the item width. */
export const SCALES: { id: AutotuneScale; label: string }[] = [
  { id: 'chrom', label: 'CHR' },
  { id: 'maj', label: 'MAJ' },
  { id: 'min', label: 'MIN' },
  { id: 'minHarm', label: 'HARM' },
  { id: 'dorian', label: 'DOR' },
];

interface KeyCell {
  /** Pitch class 0-11 the cell selects. */
  pc: number;
  /** Semitone offset from the row's home (0 = home/root). */
  offset: number;
}

/** The chromatic ramp for one row: home ± offset across start..end. */
function keyCells(home: number, start: number, end: number): KeyCell[] {
  const out: KeyCell[] = [];
  for (let o = start; o <= end; o++) {
    out.push({ pc: (((home + o) % 12) + 12) % 12, offset: o });
  }
  return out;
}

export function WavesAutotune(props: WavesAutotuneProps) {

  /**
   * Key and scale both come from `autotune/readback.ts`: the song change and
   * our own tap, newest wins, lit fully either way. OPBridge sends key AND
   * scale on every song change that carries a key, and a tap sends at once,
   * so the newer of the two is what the plugin has.
   *
   * A song with no key and no tap lights nothing rather than a guess. Never
   * reads the `autotuneKey()` / `autotuneScale()` latches: they are hydrated
   * from localStorage, and lighting a cell from them is the phantom the
   * operator reported on transpose and capo on 2026-09-21.
   */
  const shownKey = (): number | null => autotuneKeyShown();
  const shownScale = () => autotuneScaleShown();
  /** Why the key rows are dark, when the song gave a definite reason
   *  (operator 2026-09-28: "useful to know that no key has been sent"). Only
   *  while nothing is lit - a newer tap lights the key and this goes away. */
  const noKey = () => {
    if (shownKey() !== null) return null;
    const s = songAutotuneStatus();
    const why = songAutotuneWhy(s);
    return why ? { why, warn: s.kind !== 'no-key' } : null;
  };
  // Show-mode guard (SD-5): first tap on a key/scale arms it (2s), second
  // fires. Keys + scales share one guard but namespaced (`k:` / `s:`) so
  // arming a key doesn't count as arming a scale.
  const guard = createDiscreteGuard(() => !!props.item.guard);

  const rows = (): number[] => [props.item.row1Home, props.item.row2Home];
  const placement = () => props.item.scalePlacement ?? 'bottom';
  const isSide = () => placement() === 'left' || placement() === 'right';
  const keyCount = () => props.item.end - props.item.start + 1;

  function onKeyDown(pc: number, e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    if (!guard.gate(`k:${pc}`)) return; // guarded: first tap only armed
    // Relights every WavesAutotune row + dispatches CC 55 on this item's
    // channel, fanned out to this row's configured bridge(s) (2026-08-13).
    setAutotuneKey(pc, props.item.channel ?? 2, props.item.bridgeIds);
  }

  function onScaleDown(scale: AutotuneScale, e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    if (!guard.gate(`s:${scale}`)) return; // guarded: first tap only armed
    // CC 56 for min/maj, CC 57 for chrom - the session owns that split.
    setAutotuneScale(scale, props.item.channel ?? 2, props.item.bridgeIds);
  }

  // ─── Tune strength (CC 6) ────────────────────────────────────────────
  // Continuous, so it is a drag rather than a tap, and it deliberately does
  // NOT go through `guard`: the show-mode guard exists to stop a fat-finger
  // discrete jump (wrong KEY mid-song is unrecoverable), whereas a strength
  // nudge is both audible and instantly correctable by dragging back. Arming
  // a fader would make it useless for the one thing a fader is for.
  let trackRef: HTMLDivElement | undefined;

  function strengthFromClientX(clientX: number): number {
    if (!trackRef) return 0;
    const r = trackRef.getBoundingClientRect();
    if (r.width <= 0) return 0;
    return Math.max(0, Math.min(127, Math.round(((clientX - r.left) / r.width) * 127)));
  }

  function onStrengthDown(e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    setAutotuneStrength(strengthFromClientX(e.clientX), props.item.channel ?? 2, props.item.bridgeIds);
  }

  function onStrengthMove(e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    // `buttons` rather than a captured flag: a pointer released outside the
    // element never fires pointerup here, and a stuck "dragging" state on a
    // control that emits MIDI would keep firing on idle mouse movement.
    if (!(e.buttons & 1)) return;
    e.stopPropagation();
    setAutotuneStrength(strengthFromClientX(e.clientX), props.item.channel ?? 2, props.item.bridgeIds);
  }

  return (
    <div class="waves-autotune" data-placement={placement()} data-token={COLOR_TOKEN[props.item.color]} {...trayAttrs(props.item)}>
      <TrayTitle item={props.item} />
      <div class="waves-autotune-keys">
        <For each={rows()}>
          {(home) => (
            <div class="transpose waves-autotune-row">
              <For each={keyCells(home, props.item.start, props.item.end)}>
                {(cell) => (
                  <button
                    type="button"
                    class="transpose-cell"
                    data-active={shownKey() === cell.pc ? 'true' : 'false'}
                    data-unknown={shownKey() === null ? 'true' : 'false'}
                    data-armed={guard.armedKey() === `k:${cell.pc}` ? 'true' : 'false'}
                    data-home={cell.offset === 0 ? 'true' : 'false'}
                    onPointerDown={(e) => onKeyDown(cell.pc, e)}
                    title={`Autotune key ${noteName(cell.pc)}`}
                  >
                    {cell.offset === 0 ? `${noteName(home)}m` : noteName(cell.pc)}
                  </button>
                )}
              </For>
            </div>
          )}
        </For>
        <Show when={noKey()}>
          {(n) => (
            <span class="waves-autotune-nokey" data-tone={n().warn ? 'warn' : 'neutral'}>
              <span class="waves-autotune-nokey-head">No key sent</span>
              <span class="waves-autotune-nokey-why">{n().why}</span>
            </span>
          )}
        </Show>
      </div>
      {/* Scale selector. In side placement its flex-basis is one cell-width
          worth of the total (1 / (keyCount + 1)) so it reads as a compact
          column; in top/bottom it's a full row that CSS grows to 1/3. */}
      <div
        class="waves-autotune-scales"
        style={isSide() ? { flex: `0 0 ${100 / (keyCount() + 1)}%` } : undefined}
      >
        <For each={SCALES}>
          {(sc) => (
            <button
              type="button"
              class="transpose-cell"
              data-active={shownScale() === sc.id ? 'true' : 'false'}
              data-armed={guard.armedKey() === `s:${sc.id}` ? 'true' : 'false'}
              onPointerDown={(e) => onScaleDown(sc.id, e)}
              title={`Autotune scale ${sc.label}`}
            >
              {sc.label}
            </button>
          )}
        </For>
      </div>
      {/* Tune strength (CC 6). Opt-in per item so an app update never
          re-flows an already-authored gig page - see `showStrength`. */}
      <Show when={props.item.showStrength}>
        <div
          class="waves-autotune-strength"
          ref={trackRef}
          onPointerDown={onStrengthDown}
          onPointerMove={onStrengthMove}
          title="Autotune strength"
        >
          <div
            class="waves-autotune-strength-fill"
            style={{ width: `${((autotuneStrength() ?? 0) / 127) * 100}%` }}
          />
          <span class="waves-autotune-strength-label">
            {/* "--" not "0" when unset: never touched and fully off sound
                identical on a bar but mean opposite things on the rig. */}
            STRENGTH {autotuneStrength() === null ? '--' : autotuneStrength()}
          </span>
        </div>
      </Show>
    </div>
  );
}
